import { createSelector } from 'reselect';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Dispatch } from 'redux';
import { frontloadConnect } from 'react-frontload';
import { PageTrendAggregation } from 'components/pages/PageTaxonomyAggregation';
import {
  userShortcutsUrlsSelector,
  userRolesSelector,
  isAuthSelector,
  userEmailSelector,
  userFVSSectionMemberSelector,
  userOktaIdSelector,
} from 'modules/user/selectors';
import {
  aggsPageContentSelector,
  contentLoadingSelector,
  trendContentListSelector,
  skipContentFrontloadRequestSelector,
} from 'modules/content/selectors';
import { fetchAggsPageContent, resetSkipContentFrontloadRequest } from 'modules/content/actions';
import { trendFromParamsSelectorFactory, pageTrendAggsTrendSlugSelector } from 'modules/router';
import { emptyObject, getPath } from 'utils';
import { Routes } from 'constants/index';
import { toggleLoginReload } from 'modules/layouts';

const mapActionCreators = (dispatch: Dispatch) => ({
  async fetchAggsPageContent(): Promise<void> {
    await dispatch(fetchAggsPageContent()).catch(() => dispatch(push(getPath(Routes.NOT_FOUND))));
  },

  toggleLoginReload(shouldReload: boolean): void {
    dispatch(toggleLoginReload(shouldReload));
  },
  resetSkipContentFrontloadRequest(): void {
    dispatch(resetSkipContentFrontloadRequest());
  },
});

const mapStateToProps = (initState: State.Root) => {
  const trendFromParamsSelector = trendFromParamsSelectorFactory(pageTrendAggsTrendSlugSelector);

  const userHasShortcutSelector = createSelector(
    [userShortcutsUrlsSelector, pageTrendAggsTrendSlugSelector],
    (userShortcutsUrls: string[], trendSlug: string): boolean => {
      if (!userShortcutsUrls || !userShortcutsUrls.length || !trendSlug) return false;
      return userShortcutsUrls.includes(`${trendSlug}`);
    }
  );

  const userRoles: string[] | undefined = userRolesSelector(initState);
  const userRolesStr: string = userRoles ? userRoles.join() : '';

  return (state: State.Root) => {
    const trendSlug: string = pageTrendAggsTrendSlugSelector(state);

    const trend: State.Trend | null = trendFromParamsSelector(state);
    const { id: trendId, name: trendName = '', description: trendDescription = '' }: any = trend || emptyObject;

    return {
      loading: contentLoadingSelector(state),
      trendSlug,
      trendId,
      trendName,
      trendDescription,
      aggsPageContent: aggsPageContentSelector(state),
      hasShortcut: userHasShortcutSelector(state),
      userRolesStr,
      isAuth: isAuthSelector(state),
      topicContentList: trendContentListSelector(state),
      userEmail: userEmailSelector(state),
      fvsMembership: userFVSSectionMemberSelector(state),
      oktaId: userOktaIdSelector(state),
      skipContentFrontloadRequest: skipContentFrontloadRequestSelector(state),
    };
  };
};

const frontload = async (props: any) => {
  if (props.skipContentFrontloadRequest) return props.resetSkipContentFrontloadRequest();
  if (props.trendSlug) {
    return props.fetchAggsPageContent();
  }
};

const options = {
  onUpdate: true,
  _experimental_updateFunc: (prevProps: any, newProps: any) => {
    return prevProps.trendSlug !== newProps.trendSlug || prevProps.isAuth !== newProps.isAuth;
  },
};

export const PageTrendAggregationContainer = connect(
  mapStateToProps,
  mapActionCreators
)(frontloadConnect(frontload, options)(PageTrendAggregation));
